import { useMemo, useState, type ReactElement, type RefObject } from 'react'
import { $createCodeNode } from '@lexical/code-core'
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/extension'
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { INSERT_TABLE_COMMAND } from '@lexical/table'
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  type LexicalEditor
} from 'lexical'
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  CheckSquare2,
  Code2,
  Heading1,
  Heading2,
  Heading3,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Table2
} from 'lucide-react'
import { createPortal } from 'react-dom'

class ContractCommandOption extends MenuOption {
  description: string
  keywords: string[]
  onSelect: (editor: LexicalEditor) => void

  constructor(
    key: string,
    title: string,
    options: {
      description: string
      icon: ReactElement
      keywords: string[]
      onSelect: (editor: LexicalEditor) => void
    }
  ) {
    super(key)
    this.title = title
    this.description = options.description
    this.icon = options.icon
    this.keywords = options.keywords
    this.onSelect = options.onSelect
  }
}

function setBlock(type: 'paragraph' | 'h1' | 'h2' | 'h3' | 'quote' | 'code') {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return
  }
  if (type === 'paragraph') {
    $setBlocksType(selection, $createParagraphNode)
  }
  if (type === 'quote') {
    $setBlocksType(selection, $createQuoteNode)
  }
  if (type === 'h1' || type === 'h2' || type === 'h3') {
    $setBlocksType(selection, () => $createHeadingNode(type))
  }
  if (type === 'code') {
    $setBlocksType(selection, () => $createCodeNode())
  }
}

function getCommandOptions(editor: LexicalEditor, onRequestLink: () => void) {
  return [
    new ContractCommandOption('paragraph', '正文', {
      description: '恢复为普通段落',
      icon: <Pilcrow aria-hidden size={17} />,
      keywords: ['paragraph', 'normal', 'p', '正文', '段落'],
      onSelect: () => setBlock('paragraph')
    }),
    new ContractCommandOption('heading-1', '标题 1', {
      description: '大号章节标题',
      icon: <Heading1 aria-hidden size={17} />,
      keywords: ['heading', 'title', 'h1', '标题', '一级'],
      onSelect: () => setBlock('h1')
    }),
    new ContractCommandOption('heading-2', '标题 2', {
      description: '中号章节标题',
      icon: <Heading2 aria-hidden size={17} />,
      keywords: ['heading', 'title', 'h2', '标题', '二级'],
      onSelect: () => setBlock('h2')
    }),
    new ContractCommandOption('heading-3', '标题 3', {
      description: '小号章节标题',
      icon: <Heading3 aria-hidden size={17} />,
      keywords: ['heading', 'title', 'h3', '标题', '三级'],
      onSelect: () => setBlock('h3')
    }),
    new ContractCommandOption('bullet-list', '项目符号列表', {
      description: '创建无序条款清单',
      icon: <List aria-hidden size={17} />,
      keywords: ['bullet', 'unordered', 'list', '项目', '无序', '列表'],
      onSelect: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    }),
    new ContractCommandOption('number-list', '编号列表', {
      description: '创建有序条款清单',
      icon: <ListOrdered aria-hidden size={17} />,
      keywords: ['number', 'ordered', 'list', '编号', '有序', '列表'],
      onSelect: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    }),
    new ContractCommandOption('check-list', '待办列表', {
      description: '创建可勾选的事项清单',
      icon: <CheckSquare2 aria-hidden size={17} />,
      keywords: ['check', 'todo', 'task', '待办', '清单'],
      onSelect: () => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
    }),
    new ContractCommandOption('quote', '引用', {
      description: '突出显示一段引用内容',
      icon: <Quote aria-hidden size={17} />,
      keywords: ['quote', 'blockquote', '引用'],
      onSelect: () => setBlock('quote')
    }),
    new ContractCommandOption('code', '代码块', {
      description: '插入等宽代码区块',
      icon: <Code2 aria-hidden size={17} />,
      keywords: ['code', 'codeblock', '代码'],
      onSelect: () => setBlock('code')
    }),
    new ContractCommandOption('divider', '分割线', {
      description: '插入一条水平分割线',
      icon: <Minus aria-hidden size={17} />,
      keywords: ['divider', 'horizontal', 'rule', '分割线'],
      onSelect: () => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)
    }),
    new ContractCommandOption('table', '表格', {
      description: '插入一个 3 × 3 表格',
      icon: <Table2 aria-hidden size={17} />,
      keywords: ['table', 'grid', '表格'],
      onSelect: () => editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: '3', rows: '3' })
    }),
    new ContractCommandOption('link', '链接', {
      description: '为选中文本设置链接',
      icon: <Link2 aria-hidden size={17} />,
      keywords: ['link', 'url', '链接', '网址'],
      onSelect: onRequestLink
    }),
    ...[
      { icon: <AlignLeft aria-hidden size={17} />, key: 'align-left', label: '左对齐', value: 'left' as const },
      { icon: <AlignCenter aria-hidden size={17} />, key: 'align-center', label: '居中对齐', value: 'center' as const },
      { icon: <AlignRight aria-hidden size={17} />, key: 'align-right', label: '右对齐', value: 'right' as const },
      {
        icon: <AlignJustify aria-hidden size={17} />,
        key: 'align-justify',
        label: '两端对齐',
        value: 'justify' as const
      }
    ].map(
      (option) =>
        new ContractCommandOption(option.key, option.label, {
          description: '调整当前段落的排版方式',
          icon: option.icon,
          keywords: ['align', 'text', '对齐', option.label],
          onSelect: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, option.value)
        })
    )
  ]
}

function CommandMenu({
  itemProps,
  matchingString
}: {
  itemProps: {
    options: ContractCommandOption[]
    selectedIndex: number | null
    selectOptionAndCleanUp: (option: ContractCommandOption) => void
    setHighlightedIndex: (index: number) => void
  }
  matchingString: string
}) {
  const { options, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex } = itemProps

  if (options.length === 0) {
    return (
      <div className="contract-slash-menu border-border bg-surface text-content-muted w-72 rounded-xl border p-3 text-sm shadow-lg">
        没有匹配的指令
      </div>
    )
  }

  return (
    <div
      aria-label="编辑指令"
      className="contract-slash-menu border-border bg-surface w-80 overflow-hidden rounded-xl border shadow-lg"
      id="contract-slash-menu"
      role="listbox"
    >
      <div className="border-border bg-surface-muted text-content-soft border-b px-3 py-2 text-[0.6875rem] font-medium tracking-[0.12em] uppercase">
        {matchingString ? `匹配 /${matchingString}` : '插入内容'}
      </div>
      <div className="max-h-80 overflow-y-auto p-1.5">
        {options.map((option, index) => (
          <button
            aria-selected={selectedIndex === index}
            className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors ${
              selectedIndex === index ? 'text-accent bg-[var(--accent-soft)]' : 'text-content hover:bg-surface-muted'
            }`}
            id={`contract-slash-option-${index}`}
            key={option.key}
            onClick={() => selectOptionAndCleanUp(option)}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setHighlightedIndex(index)}
            ref={option.setRefElement}
            role="option"
            tabIndex={-1}
          >
            <span className="bg-surface-muted text-content-muted flex size-7 shrink-0 items-center justify-center rounded-md">
              {option.icon}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">{option.title}</span>
              <span className="text-content-soft mt-0.5 block truncate text-xs">{option.description}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="border-border text-content-soft border-t px-3 py-1.5 text-[0.6875rem]">
        ↑↓ 选择 · Enter 插入 · Esc 关闭
      </div>
    </div>
  )
}

export function ContractLexicalSlashMenu({ onRequestLink }: { onRequestLink: () => void }) {
  const [editor] = useLexicalComposerContext()
  const [queryString, setQueryString] = useState<string | null>(null)
  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    allowWhitespace: true,
    minLength: 0
  })
  const options = useMemo(() => {
    const allOptions = getCommandOptions(editor, onRequestLink)
    if (!queryString) {
      return allOptions
    }
    const query = queryString.toLocaleLowerCase()
    return allOptions.filter((option) => {
      const title = String(option.title).toLocaleLowerCase()
      return title.includes(query) || option.keywords.some((keyword) => keyword.toLocaleLowerCase().includes(query))
    })
  }, [editor, onRequestLink, queryString])
  return (
    <LexicalTypeaheadMenuPlugin<ContractCommandOption>
      menuRenderFn={renderCommandMenu}
      onQueryChange={setQueryString}
      onSelectOption={(option, nodeToRemove, closeMenu) => {
        editor.update(() => {
          nodeToRemove?.remove()
          option.onSelect(editor)
          closeMenu()
        })
      }}
      options={options}
      triggerFn={checkForTriggerMatch}
    />
  )
}

// Lexical 的 menuRenderFn 在其菜单锚点创建后调用；DOM ref 由 Lexical 管理。
function renderCommandMenu(
  anchorElementRef: RefObject<HTMLElement | null>,
  itemProps: Parameters<typeof CommandMenu>[0]['itemProps'],
  matchingString: string
) {
  const anchor = anchorElementRef.current
  return anchor ? createPortal(<CommandMenu itemProps={itemProps} matchingString={matchingString} />, anchor) : null
}
